import { pointAtDistance, type SliderPath } from '@shared/osuCurves';
import type { PlayfieldRect } from '../circlesPlayfieldGeometry';
import { SLIDER_BODY_INNER_RATIO } from './circlesRenderConstants';
import { shadeHex } from './circlesRenderColour';
import type { RenderQuality } from '../../rendering/renderQuality';

/**
 * Offscreen slider body renderer. Owns a single reusable buffer that
 * grows to peak capacity across frames — splitting this out from the
 * main slider renderer keeps each file under the size budget and
 * isolates the offscreen-compositing trick that makes self-overlap
 * read correctly.
 *
 * Why offscreen rendering matters:
 *   - Direct strokes on the main canvas compound alpha wherever the
 *     slider path crosses itself (teardrop / horseshoe / 6 shapes).
 *     The crossings produce noticeably darker blobs that misread as
 *     "two sliders" instead of "one curve looping back".
 *   - Painting both border + inner-fill strokes into an offscreen
 *     buffer at full opacity collapses overlapping segments to the
 *     same colour as non-overlapping ones. The resulting offscreen
 *     image is a true constant-width track.
 *   - We then blit that single-image-with-alpha onto the main
 *     canvas, so a slider crossing a still-visible upcoming circle
 *     can let it read through (controlled by the caller's
 *     `globalAlpha`), without ever stacking opacity on itself.
 */
export class SliderBodyRenderer {
  /**
   * Reusable offscreen canvas. Sized lazily on first use; resized
   * when the next slider needs more pixels than the current capacity.
   * We never shrink — keeping peak capacity avoids reallocation
   * churn on charts with mixed slider sizes.
   */
  private buffer: HTMLCanvasElement | null = null;
  private reducedMotion: boolean;
  private quality: RenderQuality;

  constructor(reducedMotion: boolean, quality: RenderQuality) {
    this.reducedMotion = reducedMotion;
    this.quality = quality;
  }

  /**
   * Render the slider body (outer border + inner fill) onto the
   * offscreen buffer at full opacity, then composite the result onto
   * `ctx` at its current `globalAlpha` (the caller sets that to the
   * desired fade-in/out value).
   *
   * The offscreen canvas is sized to the tight bounding box of the
   * visible segment plus a `bodyWidth` padding on each side (so stroke
   * caps aren't clipped). Capacity-only growth — we never shrink the
   * canvas to avoid reallocation churn on charts that mix big and
   * small sliders.
   */
  draw(
    ctx: CanvasRenderingContext2D,
    path: SliderPath,
    drawLength: number,
    field: PlayfieldRect,
    colour: string,
    bodyWidth: number,
    dpr: number,
  ): void {
    // Tight bounding box of the visible path in CSS px.
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const pts: { x: number; y: number }[] = [];
    for (let i = 0; i < path.points.length; i++) {
      const cumLen = path.cumulativeLength[i];
      const p = path.points[i];
      if (cumLen > drawLength) {
        const endPoint = pointAtDistance(path, drawLength);
        const ex = field.x + endPoint.x * field.scale;
        const ey = field.y + endPoint.y * field.scale;
        pts.push({ x: ex, y: ey });
        if (ex < minX) {
          minX = ex;
        }
        if (ey < minY) {
          minY = ey;
        }
        if (ex > maxX) {
          maxX = ex;
        }
        if (ey > maxY) {
          maxY = ey;
        }
        break;
      }
      const px = field.x + p.x * field.scale;
      const py = field.y + p.y * field.scale;
      pts.push({ x: px, y: py });
      if (px < minX) {
        minX = px;
      }
      if (py < minY) {
        minY = py;
      }
      if (px > maxX) {
        maxX = px;
      }
      if (py > maxY) {
        maxY = py;
      }
    }
    if (pts.length === 0 || !Number.isFinite(minX)) {
      return;
    }
    // Pad by full bodyWidth so stroke caps + the shadow blur don't
    // clip on the offscreen edges. dpr matters: we want offscreen
    // pixels at the same density as the main canvas.
    const pad = Math.ceil(bodyWidth + 8);
    const cssW = Math.ceil(maxX - minX + pad * 2);
    const cssH = Math.ceil(maxY - minY + pad * 2);
    if (cssW <= 0 || cssH <= 0) {
      return;
    }
    const pixW = Math.max(1, Math.ceil(cssW * dpr));
    const pixH = Math.max(1, Math.ceil(cssH * dpr));
    // Reuse the canvas; grow on demand. Assigning a very large
    // `canvas.width` can throw under extreme memory pressure;
    // `getContext` itself returns `null` rather than throwing. Both
    // failures degrade gracefully to "no slider body this frame" so
    // the rest of the slider (head, tail, follow circle, arrows)
    // still renders — the body is the heaviest pixel cost and the
    // most replaceable.
    if (!this.buffer) {
      this.buffer = document.createElement('canvas');
    }
    const off = this.buffer;
    if (off.width < pixW || off.height < pixH) {
      off.width = Math.max(off.width, ((pixW + 63) >> 6) << 6);
      off.height = Math.max(off.height, ((pixH + 63) >> 6) << 6);
    }
    const offCtx = off.getContext('2d');
    if (!offCtx) {
      console.warn(
        '[CirclesRenderer] Offscreen slider canvas getContext returned null; skipping body draw',
      );
      return;
    }
    // Clear ONLY the region we'll draw into — saves cost on large
    // peak-capacity canvases.
    offCtx.save();
    offCtx.setTransform(1, 0, 0, 1, 0, 0);
    offCtx.clearRect(0, 0, pixW, pixH);
    offCtx.scale(dpr, dpr);
    // Translate so playfield-space `(minX, minY)` maps to `(pad, pad)`
    // in offscreen-space. Net effect: the slider's bounding box sits
    // centred in the offscreen with `pad` margin all round.
    offCtx.translate(-minX + pad, -minY + pad);
    offCtx.lineCap = 'round';
    offCtx.lineJoin = 'round';

    // Outer stroke — full body width at full opacity (combo colour).
    // No shadow blur on the offscreen pass: shadows would also stack
    // on overlap, defeating the whole point of the technique. We
    // add the rim glow on the main-canvas composite below instead.
    offCtx.strokeStyle = colour;
    offCtx.lineWidth = bodyWidth;
    offCtx.beginPath();
    offCtx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) {
      offCtx.lineTo(pts[i].x, pts[i].y);
    }
    offCtx.stroke();

    // Inner stroke — darker shaded combo colour, full opacity,
    // narrower so a coloured rim of the outer stroke remains visible.
    // The dark inner gives the head's combo number something to read
    // against and makes the track look like a 3D channel rather than
    // a flat noodle.
    offCtx.strokeStyle = shadeHex(colour, 0.35);
    offCtx.lineWidth = bodyWidth * SLIDER_BODY_INNER_RATIO;
    offCtx.beginPath();
    offCtx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) {
      offCtx.lineTo(pts[i].x, pts[i].y);
    }
    offCtx.stroke();

    offCtx.restore();

    // Composite back onto the main canvas. drawImage with explicit
    // src/dst rects so we only read the populated region of the
    // offscreen (the canvas itself may be larger than the slider
    // bbox from previous frames at peak capacity).
    //
    // Rim glow is gated on `chromeShadows`: blurring a full slider-
    // bbox-sized drawImage is the single most expensive frame op on
    // mobile (Gaussian blur over thousands of source pixels per
    // visible slider, every frame). On low tier the coloured outer
    // rim already reads as a defined ribbon without the halo.
    if (!this.reducedMotion && this.quality.chromeShadows) {
      ctx.shadowColor = colour;
      ctx.shadowBlur = 14;
    }
    ctx.drawImage(off, 0, 0, pixW, pixH, minX - pad, minY - pad, cssW, cssH);
    ctx.shadowBlur = 0;
  }
}
